import { useMemo, useState, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router'
import type { CreateOrderRequest, LineMerchandise, OrderLineInput, PaymentMethod, ShopifyOrderSummary, VisitRow } from '@fitting-room/contracts'
import { defaultRequiredDate, normalizeShopifyOrderNo } from '@fitting-room/contracts/order-math'
import { ChevronLeft, Plus, Search, X } from 'lucide-react'
import { AssigneeSelect } from '../../components/admin/AssigneeSelect'
import { Chip } from '../../components/admin/Chip'
import { DatePicker } from '../../components/admin/DatePicker'
import { FormField, FormSheet } from '../../components/admin/FormSheet'
import { GarmentPicker, RackPicker } from '../../components/admin/orders/OrderLineDialog'
import { ShopifyOrderField } from '../../components/admin/payments/ShopifyOrderField'
import { useAppointment, useAssignees, useBackOffice, useCreateOrder } from '../../lib/adminQueries'
import { appointmentHref, dollarsFromMinor, minorFromDollars } from '../../lib/appointments'
import { BOUTIQUE_LOCALE, boutiqueDayOf, boutiqueTime, boutiqueToday } from '../../lib/boutique'
import { shortDay } from '../../lib/commission'
import { formatDay } from '../../lib/format'
import {
  GROUP_LABEL,
  LINE_KIND_LABEL,
  MEASUREMENT_LABEL,
  balanceDueSource,
  checkDraft,
  depositFromPercent,
  draftIsBlank,
  draftTotals,
  expectedPickupSource,
  formatCents,
  formatRate,
  kindsForGroup,
  newDraft,
  newOrderPickupDay,
  percentOfTotal,
  type LineDraft,
} from '../../lib/orders'
import { DEFAULT_PAYMENT_METHOD, PAYMENT_METHOD_LABEL, PAYMENT_METHOD_ORDER, shopifyOrderNoMethodNote } from '../../lib/payments'
import { useStaffToken } from '../../lib/staffToken'

/** Loose on purpose: the API has the real rule, this only catches the obvious typo. */
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const MEASUREMENTS = ['bust', 'waist', 'hip', 'wh', 'hh'] as const

/** The deposit a new order starts with: half, the boutique's usual. */
const DEFAULT_DEPOSIT_PERCENT = '50'

type PaymentType = 'paid_in_full' | 'deposit' | 'none'

/**
 * A new order, written on one page laid out like the invoice it becomes
 * (`docs/22` § "Orders"): who it is for; the dresses, the accessories and
 * the bridal heels, each with "Add more"; the customer's measurements and the
 * other request; the totals; and the payment taken — paid in full, or a
 * deposit with the remaining balance and the day it is due, which is the
 * expected pickup unless somebody picks another. Written with one request,
 * all of it or none (`POST /api/admin/orders`).
 *
 * From an appointment (`?visit=<id>`, the appointment drawer's "Create
 * order") everything it knows is filled in — the customer, the wedding date,
 * the bridal stylist — and the two are linked when the order is written.
 */
export function NewOrderPage() {
  const [params] = useSearchParams()
  const visitId = params.get('visit')
  const { token } = useStaffToken()
  const appointment = useAppointment(token, visitId)

  return (
    <div>
      <Link to="/admin/orders" className="st-btn-quiet -ml-2 mb-3 inline-flex">
        <ChevronLeft size={14} aria-hidden />
        Orders
      </Link>
      {visitId && appointment.isPending ? <p className="st-meta">Loading the appointment…</p> : null}
      {visitId && appointment.isError ? (
        <p className="st-alert mb-4" role="alert">
          Couldn&rsquo;t load that appointment, so nothing is filled in. You can still write the order.
        </p>
      ) : null}
      {!visitId || !appointment.isPending ? (
        <NewOrderForm key={appointment.data?.visit.id ?? 'blank'} fromVisit={appointment.data?.visit} />
      ) : null}
    </div>
  )
}

function NewOrderForm({ fromVisit }: { readonly fromVisit?: VisitRow | undefined }) {
  const { token } = useStaffToken()
  const navigate = useNavigate()
  const members = useAssignees(token).data?.members ?? []
  const backOffice = useBackOffice(token)
  const settings = backOffice.data
  const create = useCreateOrder(token)
  const today = boutiqueToday()
  const taxRateBps = settings?.salesTaxRateBps ?? 0
  const maxPct = settings?.maxDiscountPct ?? 100

  // Who it is for.
  const [name, setName] = useState(fromVisit?.customerName ?? '')
  const [phone, setPhone] = useState(fromVisit?.customerPhone ?? '')
  const [email, setEmail] = useState(fromVisit?.customerEmail ?? '')
  const [weddingDate, setWeddingDate] = useState(fromVisit?.weddingDate ?? '')
  // `undefined` until somebody picks one: until then it follows the wedding date.
  const [requiredDate, setRequiredDate] = useState<string | undefined>(undefined)
  const [address, setAddress] = useState('')
  const defaultStaff = fromVisit?.assigneeId && members.some((m) => m.id === fromVisit.assigneeId && m.active) ? fromVisit.assigneeId : null
  const [salesStaffId, setSalesStaffId] = useState<string | null | undefined>(undefined)
  const staff = salesStaffId === undefined ? defaultStaff : salesStaffId
  const shownRequired = requiredDate ?? defaultRequiredDate(weddingDate || undefined) ?? ''
  // `undefined` until somebody picks one: until then it is worked out, and
  // keeps being worked out on every read once the order is written.
  const [expectedPickupDate, setExpectedPickupDate] = useState<string | undefined>(undefined)
  const shownPickup = expectedPickupDate ?? newOrderPickupDay(today)

  // What they bought.
  const [drafts, setDrafts] = useState<LineDraft[]>(() => [newDraft('dress')])
  const [heels, setHeels] = useState(false)
  const [measurements, setMeasurements] = useState<Record<(typeof MEASUREMENTS)[number], string>>({ bust: '', waist: '', hip: '', wh: '', hh: '' })
  const [otherRequest, setOtherRequest] = useState('')
  const [note, setNote] = useState('')

  // The payment.
  const [paymentType, setPaymentType] = useState<PaymentType>('deposit')
  const [depositBy, setDepositBy] = useState<'percent' | 'amount'>('percent')
  const [depositPercent, setDepositPercent] = useState(DEFAULT_DEPOSIT_PERCENT)
  const [depositAmount, setDepositAmount] = useState('')
  const [paidAt, setPaidAt] = useState(today)
  const [method, setMethod] = useState<PaymentMethod>(DEFAULT_PAYMENT_METHOD)
  const [shopifyOrderNo, setShopifyOrderNo] = useState('')
  const [shopifyOrder, setShopifyOrder] = useState<ShopifyOrderSummary | null>(null)
  const [balanceDueDate, setBalanceDueDate] = useState<string | undefined>(undefined)

  const [submitted, setSubmitted] = useState(false)

  const lines = useMemo(() => drafts.filter((d) => d.merchandise !== 'shoes' || heels), [drafts, heels])
  const totals = draftTotals(lines, taxRateBps, maxPct)
  const total = totals.totalMinor
  const depositMinor =
    depositBy === 'percent' ? depositFromPercent(total, Number(depositPercent) || 0) : (minorFromDollars(depositAmount) ?? 0)
  const shownPercent = depositBy === 'percent' ? depositPercent : percentOfTotal(total, depositMinor)
  const shownAmount = depositBy === 'amount' ? depositAmount : depositMinor > 0 ? dollarsFromMinor(depositMinor) : ''
  const paidMinor = paymentType === 'paid_in_full' ? total : paymentType === 'deposit' ? depositMinor : 0
  const balanceMinor = Math.max(total - paidMinor, 0)

  const update = (key: string, patch: Partial<LineDraft>) => setDrafts((prev) => prev.map((d) => (d.key === key ? { ...d, ...patch } : d)))
  const addLine = (group: LineMerchandise) => setDrafts((prev) => [...prev, newDraft(group)])
  const removeLine = (key: string) => setDrafts((prev) => prev.filter((d) => d.key !== key))
  const toggleHeels = (on: boolean) => {
    setHeels(on)
    if (on && !drafts.some((d) => d.merchandise === 'shoes')) addLine('shoes')
  }

  // What is wrong, worked out on every render and shown once somebody presses Write.
  const checks = new Map(lines.map((d) => [d.key, checkDraft(d, maxPct)]))
  const problems: Record<string, string> = {}
  if (!name.trim()) problems.name = 'The customer’s name, so the order can be found again.'
  if (email.trim() && !EMAIL.test(email.trim())) problems.email = "That doesn't look like an email address."
  const sending = lines.filter((d) => !draftIsBlank(d))
  const badLine = sending.some((d) => !checks.get(d.key)?.input)
  if (badLine) problems.lines = 'Some lines need a price, or a number that isn’t one. They’re marked below.'
  if (paymentType === 'paid_in_full' && total <= 0) problems.payment = 'There’s nothing to pay in full yet. Add what they bought first.'
  if (paymentType === 'deposit') {
    if (depositBy === 'amount' && minorFromDollars(depositAmount) === undefined) problems.payment = 'A deposit in dollars, like 1,000.'
    else if (depositMinor <= 0) problems.payment = 'A deposit of more than $0 — or pick “No payment yet”.'
    else if (depositMinor > total) problems.payment = `A deposit can’t be more than the total, ${formatCents(total)}.`
  }

  const pickShopify = (order: ShopifyOrderSummary | null) => {
    setShopifyOrder(order)
    setShopifyOrderNo(order ? (normalizeShopifyOrderNo(order.name) ?? '') : '')
    if (!order) return
    const day = boutiqueDayOf(order.createdAt)
    setPaidAt(day < today ? day : today)
    if (paymentType === 'deposit') {
      setDepositBy('amount')
      setDepositAmount(dollarsFromMinor(order.totalMinor))
    }
  }
  const typeShopify = (text: string) => {
    setShopifyOrder(null)
    setShopifyOrderNo(text)
  }

  const submit = (event: FormEvent) => {
    event.preventDefault()
    setSubmitted(true)
    if (Object.keys(problems).length > 0) return
    const inputs = sending.map((d) => checks.get(d.key)?.input).filter((i): i is OrderLineInput => Boolean(i))
    const cleaned = Object.fromEntries(MEASUREMENTS.flatMap((key) => (measurements[key].trim() ? [[key, measurements[key].trim()]] : [])))
    const shopifyNo = normalizeShopifyOrderNo(shopifyOrderNo)
    const body: CreateOrderRequest = {
      ...(fromVisit ? { visitId: fromVisit.id } : {}),
      customerName: name.trim(),
      ...(phone.trim() ? { customerPhone: phone.trim() } : {}),
      ...(email.trim() ? { customerEmail: email.trim() } : {}),
      ...(weddingDate ? { weddingDate } : {}),
      ...(address.trim() ? { shippingAddress: address.trim() } : {}),
      // Cleared by hand is sent as `null`: left out, the API would put the
      // wedding date − 45 days back.
      ...(shownRequired ? { requiredDate: shownRequired } : requiredDate === '' ? { requiredDate: null } : {}),
      ...(Object.keys(cleaned).length > 0 ? { measurements: cleaned } : {}),
      ...(otherRequest.trim() ? { otherRequest: otherRequest.trim() } : {}),
      // Each stored only when somebody set it: otherwise the pickup is worked
      // out on every read, moving when the supplier's arrival date comes in,
      // and the balance falls due on it.
      ...(expectedPickupDate ? { expectedPickupDate } : {}),
      ...(balanceDueDate && paymentType !== 'paid_in_full' && balanceMinor > 0 ? { balanceDueDate } : {}),
      // Untouched, it is left to the API, which takes the appointment's
      // stylist itself — the team list may not have loaded to show it here.
      ...(salesStaffId !== undefined ? { salesStaffId } : {}),
      ...(note.trim() ? { note: note.trim() } : {}),
      lines: inputs,
      ...(paymentType === 'none'
        ? {}
        : {
            payment: {
              type: paymentType,
              amountMinor: paidMinor,
              paidAt,
              method,
              ...(shopifyNo ? { shopifyOrderNo: shopifyNo } : {}),
              ...(method === 'shopify' && shopifyOrder ? { shopifyOrderId: shopifyOrder.id, reference: shopifyOrder.name } : {}),
            },
          }),
    }
    create.mutate(body, { onSuccess: (detail) => void navigate(`/admin/orders/${detail.order.id}`, { replace: true }) })
  }

  const shown = submitted ? problems : {}

  return (
    <form onSubmit={submit} noValidate className="space-y-4">
      <div>
        <div className="st-eyebrow mb-1">
          {fromVisit
            ? `From the appointment on ${formatDay(boutiqueDayOf(fromVisit.scheduledAt), BOUTIQUE_LOCALE)} at ${boutiqueTime(fromVisit.scheduledAt)} — they'll be linked`
            : 'Invoice'}
        </div>
        <h1 className="st-display text-3xl">New order</h1>
      </div>

      {/* Who it is for, as the top of the invoice reads. */}
      <section className="st-card px-4 py-3" aria-labelledby="new-order-customer">
        <h2 id="new-order-customer" className="st-eyebrow mb-2.5">
          Customer
        </h2>
        <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-3">
          <FormField label="Name" error={shown.name}>
            <input className="st-input" value={name} onChange={(event) => setName(event.target.value)} autoComplete="off" autoFocus={!fromVisit} />
          </FormField>
          <div className="mb-3 flex flex-col gap-1">
            <span className="st-eyebrow">Required date</span>
            <div className="flex items-center">
              <DatePicker value={shownRequired} onChange={setRequiredDate} label="Required date" placeholder="Not set" onClear={() => setRequiredDate('')} />
            </div>
            <span className="st-meta text-xs">
              {requiredDate === undefined ? (weddingDate ? 'Wedding date − 45 days' : 'Fills in from the wedding date') : requiredDate ? 'Set by hand' : 'Cleared by hand'}
            </span>
          </div>
          <FormField label="Shipping address">
            <textarea className="st-input" rows={2} value={address} onChange={(event) => setAddress(event.target.value)} />
          </FormField>
          <FormField label="Email" error={shown.email}>
            <input className="st-input" type="email" value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="off" />
          </FormField>
          <FormField label="Phone">
            <input className="st-input" type="tel" value={phone} onChange={(event) => setPhone(event.target.value)} autoComplete="off" />
          </FormField>
          <div className="mb-3 flex flex-col gap-1">
            <span className="st-eyebrow">Wedding date</span>
            <div className="flex items-center">
              <DatePicker value={weddingDate} onChange={setWeddingDate} label="Wedding date" placeholder="Not set" onClear={() => setWeddingDate('')} />
            </div>
          </div>
          <FormField label="Bridal stylist" hint="Whose sale it is — the commission follows it.">
            <AssigneeSelect members={members} value={staff} onChange={setSalesStaffId} label="Bridal stylist" />
          </FormField>
          <div className="mb-3 flex flex-col gap-1">
            <span className="st-eyebrow">Expected pickup</span>
            <div className="flex items-center">
              <DatePicker
                value={shownPickup}
                onChange={setExpectedPickupDate}
                label="Expected pickup"
                onClear={expectedPickupDate ? () => setExpectedPickupDate(undefined) : undefined}
              />
            </div>
            <span className="st-meta text-xs">{expectedPickupDate ? 'Set by hand' : expectedPickupSource('default')}</span>
          </div>
        </div>
      </section>

      {/* What they bought, in the invoice's groups. */}
      {(['dress', 'accessory'] as const).map((group) => (
        <section key={group} className="st-card overflow-hidden" aria-label={GROUP_LABEL[group]} data-group={group}>
          <h2 className="st-invoice-band">{GROUP_LABEL[group]}</h2>
          <div className="space-y-2.5 px-3 py-3">
            {drafts.filter((d) => d.merchandise === group).length === 0 ? <p className="st-meta px-1 text-sm">None.</p> : null}
            {drafts
              .filter((d) => d.merchandise === group)
              .map((d) => (
                <DraftRow
                  key={d.key}
                  draft={d}
                  check={checks.get(d.key) ?? checkDraft(d, maxPct)}
                  showProblems={submitted && !draftIsBlank(d)}
                  maxPct={maxPct}
                  onChange={(patch) => update(d.key, patch)}
                  onRemove={() => removeLine(d.key)}
                />
              ))}
            <button type="button" className="st-btn-quiet -ml-1" onClick={() => addLine(group)}>
              <Plus size={14} aria-hidden />
              Add more
            </button>
          </div>
        </section>
      ))}

      <section className="st-card overflow-hidden" aria-label={GROUP_LABEL.shoes} data-group="shoes">
        <h2 className="st-invoice-band">{GROUP_LABEL.shoes}</h2>
        <div className="space-y-2.5 px-3 py-3">
          <label className="flex items-center gap-2 px-1 text-sm">
            <input type="checkbox" checked={heels} onChange={(event) => toggleHeels(event.target.checked)} />
            Bridal heels
          </label>
          {heels
            ? drafts
                .filter((d) => d.merchandise === 'shoes')
                .map((d) => (
                  <DraftRow
                    key={d.key}
                    draft={d}
                    check={checks.get(d.key) ?? checkDraft(d, maxPct)}
                    showProblems={submitted && !draftIsBlank(d)}
                    maxPct={maxPct}
                    onChange={(patch) => update(d.key, patch)}
                    onRemove={() => removeLine(d.key)}
                  />
                ))
            : null}
          {heels ? (
            <button type="button" className="st-btn-quiet -ml-1" onClick={() => addLine('shoes')}>
              <Plus size={14} aria-hidden />
              Add more
            </button>
          ) : null}
        </div>
      </section>
      {shown.lines ? (
        <p className="st-alert text-sm" role="alert">
          {shown.lines}
        </p>
      ) : null}

      {/* The customer's measurements beside the other request. */}
      <section className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
        <div className="st-card overflow-hidden">
          <h2 className="st-invoice-band">Customer measurements</h2>
          <div className="grid grid-cols-[auto_1fr] items-center gap-x-3 gap-y-1.5 px-4 py-3">
            {MEASUREMENTS.map((key) => (
              <label key={key} className="contents text-sm">
                <span className="st-meta">{MEASUREMENT_LABEL[key]}</span>
                <input
                  className="st-input tabular-nums"
                  maxLength={40}
                  value={measurements[key]}
                  onChange={(event) => setMeasurements((prev) => ({ ...prev, [key]: event.target.value }))}
                />
              </label>
            ))}
          </div>
        </div>
        <div className="st-card overflow-hidden">
          <h2 className="st-invoice-band">Other request</h2>
          <div className="px-4 py-3">
            <textarea
              className="st-input w-full"
              rows={6}
              aria-label="Other request"
              placeholder="Like: customized veil with matching lace"
              value={otherRequest}
              onChange={(event) => setOtherRequest(event.target.value)}
            />
          </div>
        </div>
      </section>

      {/* The totals, at the rate the order will keep. */}
      <section className="st-card overflow-hidden text-sm" aria-labelledby="new-order-totals">
        <h2 id="new-order-totals" className="st-invoice-band">
          Totals
        </h2>
        <div className="px-4 py-3">
          <Row label="Subtotal" value={formatCents(totals.subtotalMinor)} />
          <Row label={`Tax · ${formatRate(taxRateBps)}`} value={formatCents(totals.taxMinor)} />
          <div className="mt-1.5 flex justify-between border-t pt-2 text-base" style={{ borderColor: 'var(--st-rule)' }}>
            <span className="font-medium">Total</span>
            <span className="st-display text-xl tabular-nums" data-testid="new-order-total">
              {formatCents(total)}
            </span>
          </div>
          <p className="st-meta mt-1 text-xs">The order keeps this tax rate even if the setting changes later.</p>
        </div>
      </section>

      {/* The payment taken now. */}
      <section className="st-card px-4 py-3" aria-labelledby="new-order-payment">
        <h2 id="new-order-payment" className="st-eyebrow mb-2">
          Payment
        </h2>
        <div className="mb-3 flex flex-wrap gap-1.5" role="group" aria-label="Payment type">
          {(
            [
              ['paid_in_full', 'Paid in full'],
              ['deposit', 'Deposit paid'],
              ['none', 'No payment yet'],
            ] as const
          ).map(([value, label]) => (
            <Chip key={value} pressed={paymentType === value} onClick={() => setPaymentType(value)}>
              {label}
            </Chip>
          ))}
        </div>

        {paymentType !== 'none' ? (
          <>
            <div className="grid grid-cols-2 gap-x-3 sm:grid-cols-4">
              {paymentType === 'paid_in_full' ? (
                <div className="mb-3 flex flex-col gap-1">
                  <span className="st-eyebrow">Amount</span>
                  <span className="st-input tabular-nums" aria-label="Amount paid in full">
                    {formatCents(total)}
                  </span>
                </div>
              ) : (
                <>
                  <FormField label="Deposit">
                    <input
                      className="st-input tabular-nums"
                      inputMode="decimal"
                      placeholder="$"
                      value={shownAmount}
                      onChange={(event) => {
                        setDepositBy('amount')
                        setDepositAmount(event.target.value)
                      }}
                    />
                  </FormField>
                  <FormField label="Percent">
                    <input
                      className="st-input tabular-nums"
                      inputMode="decimal"
                      placeholder="%"
                      value={shownPercent}
                      onChange={(event) => {
                        setDepositBy('percent')
                        setDepositPercent(event.target.value.replace('%', ''))
                      }}
                    />
                  </FormField>
                </>
              )}
              <div className="mb-3 flex flex-col gap-1">
                <span className="st-eyebrow">Paid on</span>
                <div className="flex items-center">
                  <DatePicker value={paidAt} onChange={setPaidAt} label="Paid on" max={today} />
                </div>
              </div>
              <FormField label="Payment method">
                <select
                  className="st-select"
                  value={method}
                  onChange={(event) => setMethod(event.target.value as PaymentMethod)}
                >
                  {PAYMENT_METHOD_ORDER.map((m) => (
                    <option key={m} value={m}>
                      {PAYMENT_METHOD_LABEL[m]}
                    </option>
                  ))}
                </select>
              </FormField>
            </div>
            <ShopifyOrderField
              picked={shopifyOrder}
              typed={shopifyOrderNo}
              onPick={pickShopify}
              onType={typeShopify}
              note={shopifyOrderNoMethodNote(method, shopifyOrderNo)}
            />
          </>
        ) : null}

        {paymentType !== 'paid_in_full' ? (
          <div className="st-money-line mt-1 grid grid-cols-1 gap-3 text-sm sm:grid-cols-2" data-testid="new-order-balance">
            <div>
              <div className="st-eyebrow mb-1">Remaining balance</div>
              <div className="text-base font-medium tabular-nums">{formatCents(balanceMinor)}</div>
            </div>
            {/* Nothing left to pay, no day for it to be due. */}
            {balanceMinor > 0 ? (
              <div className="flex flex-col gap-1">
                <span className="st-eyebrow">Balance due</span>
                <div className="flex flex-wrap items-center gap-2">
                  <DatePicker value={balanceDueDate ?? shownPickup} onChange={setBalanceDueDate} label="Balance due date" />
                  {balanceDueDate ? (
                    <button type="button" className="st-btn-quiet text-xs" onClick={() => setBalanceDueDate(undefined)}>
                      Follow the pickup
                    </button>
                  ) : null}
                </div>
                <span className="st-meta text-xs">{balanceDueSource(balanceDueDate ? 'set' : 'pickup')}</span>
              </div>
            ) : null}
          </div>
        ) : null}
        {shown.payment ? (
          <p className="mt-2 text-xs" style={{ color: '#8d3f2c' }} role="alert">
            {shown.payment}
          </p>
        ) : null}
      </section>

      <section className="st-card px-4 py-3">
        <FormField label="Note">
          <textarea className="st-input" rows={3} value={note} onChange={(event) => setNote(event.target.value)} />
        </FormField>
      </section>

      {backOffice.isError ? (
        <p className="st-alert" role="alert">
          Couldn&rsquo;t load the sales tax rate and the discount cap, so the order can&rsquo;t be written yet. Reload the page to try again.
        </p>
      ) : null}
      {create.isError ? (
        <p className="st-alert" role="alert">
          {create.error.message}
        </p>
      ) : null}
      {submitted && Object.keys(problems).length > 0 ? (
        <p className="st-alert text-sm" role="alert">
          Not written yet — fix what&rsquo;s marked above.
        </p>
      ) : null}

      {paymentType !== 'none' && paidMinor > 0 ? (
        <p className="st-meta text-right text-xs" data-testid="new-order-payment-confirm">
          Records {paymentType === 'paid_in_full' ? 'a payment in full' : 'a deposit'} of {formatCents(paidMinor)} on {shortDay(paidAt)}.
        </p>
      ) : null}
      <div className="flex flex-wrap justify-end gap-2 pb-8">
        <Link to={fromVisit ? appointmentHref(fromVisit) : '/admin/orders'} className="st-btn-ghost">
          Cancel
        </Link>
        {/* Not before the settings are in: until then the totals, and a
            payment in full taken from them, are worked out at no sales tax
            and no discount cap. */}
        <button type="submit" className="st-btn" disabled={create.isPending || !settings}>
          {create.isPending ? 'Writing…' : settings ? 'Write the order' : 'Loading the settings…'}
        </button>
      </div>
    </form>
  )
}

function Row({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <div className="flex justify-between gap-3 py-0.5">
      <span className="st-meta">{label}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  )
}

/**
 * One line being typed: its type, style #, color, size, price, custom cost
 * and discount, and the total it comes to. A style # is enough — "Find"
 * picks one from the catalog (special order) or the rack (off the rack) and
 * fills the rest, but neither is needed. Heels are a style, a price and a
 * discount.
 */
function DraftRow({
  draft,
  check,
  showProblems,
  maxPct,
  onChange,
  onRemove,
}: {
  readonly draft: LineDraft
  readonly check: ReturnType<typeof checkDraft>
  readonly showProblems: boolean
  readonly maxPct: number
  readonly onChange: (patch: Partial<LineDraft>) => void
  readonly onRemove: () => void
}) {
  const [finding, setFinding] = useState(false)
  const shoes = draft.merchandise === 'shoes'
  const problems = showProblems ? check.problems : {}
  const kinds = kindsForGroup(draft.merchandise)
  const err = (text: string | undefined) =>
    text ? (
      <span className="text-xs" style={{ color: '#8d3f2c' }} role="alert">
        {text}
      </span>
    ) : null

  return (
    <div className="st-line-draft" data-draft={draft.merchandise}>
      <div className="grid grid-cols-2 gap-x-2.5 gap-y-2 sm:grid-cols-4 lg:grid-cols-8">
        {!shoes ? (
          <label className="flex flex-col gap-1 text-xs">
            <span className="st-meta">Type</span>
            <select
              className="st-select"
              value={draft.kind}
              onChange={(event) => onChange({ kind: event.target.value as LineDraft['kind'], garmentId: undefined, rackItemId: undefined, takenToday: false })}
            >
              {kinds.map((k) => (
                <option key={k} value={k}>
                  {LINE_KIND_LABEL[k]}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        <label className={`flex flex-col gap-1 text-xs ${shoes ? 'col-span-2 sm:col-span-2' : ''}`}>
          <span className="st-meta">{shoes ? 'Shoe style' : 'Style #'}</span>
          <span className="flex gap-1">
            <input className="st-input min-w-0 flex-1" value={draft.styleCode} autoComplete="off" onChange={(event) => onChange({ styleCode: event.target.value })} />
            {!shoes ? (
              <button type="button" className="st-iconbtn shrink-0" aria-label={draft.kind === 'off_the_rack' ? 'Find on the rack' : 'Find in the catalog'} onClick={() => setFinding(true)}>
                <Search size={14} aria-hidden />
              </button>
            ) : null}
          </span>
        </label>
        {!shoes ? (
          <>
            <label className="flex flex-col gap-1 text-xs">
              <span className="st-meta">Color</span>
              <input className="st-input" value={draft.color} autoComplete="off" onChange={(event) => onChange({ color: event.target.value })} />
            </label>
            <label className="flex flex-col gap-1 text-xs">
              <span className="st-meta">Size</span>
              <input className="st-input" value={draft.size} autoComplete="off" disabled={Boolean(draft.rackItemId)} onChange={(event) => onChange({ size: event.target.value })} />
            </label>
          </>
        ) : null}
        <label className="flex flex-col gap-1 text-xs">
          <span className="st-meta">Price</span>
          <input className="st-input tabular-nums" inputMode="decimal" placeholder="$" value={draft.price} onChange={(event) => onChange({ price: event.target.value })} />
          {err(problems.price)}
        </label>
        {!shoes ? (
          <label className="flex flex-col gap-1 text-xs">
            <span className="st-meta">Custom cost</span>
            <input className="st-input tabular-nums" inputMode="decimal" placeholder="$0" value={draft.customCost} onChange={(event) => onChange({ customCost: event.target.value })} />
            {err(problems.customCost)}
          </label>
        ) : null}
        <label className="flex flex-col gap-1 text-xs">
          <span className="st-meta">Discount{maxPct < 100 ? ` · up to ${maxPct}%` : ''}</span>
          <span className="flex gap-1">
            <select
              className="st-select w-14 shrink-0 px-1"
              aria-label="Discount as"
              value={draft.discountMode}
              onChange={(event) => onChange({ discountMode: event.target.value as LineDraft['discountMode'], ...(event.target.value === 'none' ? { discountText: '' } : {}) })}
            >
              <option value="none">—</option>
              <option value="percent">%</option>
              <option value="amount">$</option>
            </select>
            <input
              className="st-input min-w-0 flex-1 tabular-nums"
              inputMode="decimal"
              aria-label="Discount"
              disabled={draft.discountMode === 'none'}
              value={draft.discountText}
              onChange={(event) => onChange({ discountText: event.target.value })}
            />
          </span>
          {err(problems.discount)}
        </label>
        <div className="flex flex-col gap-1 text-xs">
          <span className="st-meta">Total</span>
          <span className="flex items-center justify-between gap-1">
            <span className="py-1.5 text-sm font-medium tabular-nums" data-testid="draft-total">
              {check.preview ? formatCents(check.preview.netMinor) : '—'}
            </span>
            <button type="button" className="st-iconbtn" aria-label="Remove this line" onClick={onRemove}>
              <X size={14} aria-hidden />
            </button>
          </span>
        </div>
      </div>
      {draft.rackItemId ? (
        <p className="st-meta mt-1.5 text-xs">
          Linked to a piece on the rack; it&rsquo;s held for this order.{' '}
          <button type="button" className="underline" onClick={() => onChange({ rackItemId: undefined })}>
            Unlink
          </button>
        </p>
      ) : null}
      {draft.kind === 'off_the_rack' ? (
        <label className="mt-1.5 flex items-center gap-2 text-xs">
          <input type="checkbox" checked={draft.takenToday} onChange={(event) => onChange({ takenToday: event.target.checked })} />
          The customer is taking it home today
        </label>
      ) : null}

      <FormSheet
        open={finding}
        onOpenChange={setFinding}
        title={draft.kind === 'off_the_rack' ? 'Find on the rack' : 'Find in the catalog'}
        description={draft.kind === 'off_the_rack' ? 'Optional: linking a piece holds it for this order.' : 'Optional: fills in the style # and price.'}
      >
        {finding && draft.kind === 'off_the_rack' ? (
          <RackPicker
            selected={draft.rackItemId ?? null}
            error={undefined}
            onPick={(item) => {
              onChange({
                rackItemId: item.id,
                styleCode: item.styleCode ?? item.name,
                size: item.size,
                color: item.color ?? draft.color,
                ...(item.priceMinor !== undefined ? { price: dollarsFromMinor(item.priceMinor) } : {}),
              })
              setFinding(false)
            }}
          />
        ) : null}
        {finding && draft.kind === 'made_to_order' ? (
          <GarmentPicker
            selected={draft.garmentId ?? null}
            onPick={(garment) => {
              onChange({
                garmentId: garment.id,
                styleCode: garment.code ?? garment.name,
                ...(garment.priceMinor !== undefined ? { price: dollarsFromMinor(garment.priceMinor) } : {}),
              })
              setFinding(false)
            }}
          />
        ) : null}
      </FormSheet>
    </div>
  )
}
